/** Media Library picker — browse uploaded files + fleet gallery to pick an image/video URL. */
import React, { useState, useEffect, useCallback } from 'react';
import { Input } from '../ui/input';
import { X, Image as ImageIcon, Film, Search } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export const LibraryPickerDialog = ({ open, onClose, onSelect, type = 'image' }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [source, setSource] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [libRes, fleetRes] = await Promise.all([
        fetch(`${API}/api/uploads/library?limit=120`, { credentials: 'include' }),
        fetch(`${API}/api/fleet-media/all?include_hidden=false`),
      ]);
      const lib = libRes.ok ? await libRes.json() : { files: [] };
      const fleet = fleetRes.ok ? await fleetRes.json() : { vehicles: [] };
      const out = [];
      for (const f of lib.files || []) {
        const isVideo = (f.media_type === 'video') || (f.content_type || '').startsWith('video/');
        if ((type === 'video') !== isVideo) continue;
        out.push({ url: f.url, name: f.original_filename || f.file_id, source: 'library', kind: isVideo ? 'video' : 'image' });
      }
      for (const v of fleet.vehicles || []) {
        if (type === 'image') {
          for (const img of v.images || []) out.push({ url: img.url, name: v.vehicle_name || v.vehicle_id, source: 'fleet', kind: 'image' });
        } else {
          for (const vid of v.videos || []) out.push({ url: vid.url, name: v.vehicle_name || v.vehicle_id, source: 'fleet', kind: 'video' });
        }
      }
      setItems(out);
    } catch (_) { setItems([]); }
    setLoading(false);
  }, [type]);

  useEffect(() => { if (open) load(); }, [open, load]);
  if (!open) return null;

  const filtered = items.filter(i =>
    (source === 'all' || i.source === source) &&
    (!q || i.name.toLowerCase().includes(q.toLowerCase()))
  );

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="library-picker">
      <div className="bg-white rounded-xl w-full max-w-4xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b flex items-center gap-3">
          {type === 'video' ? <Film className="h-5 w-5 text-red-600" /> : <ImageIcon className="h-5 w-5 text-red-600" />}
          <h3 className="font-bold text-lg flex-1">Choose {type === 'video' ? 'a Video' : 'an Image'} from Library</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-black" data-testid="library-picker-close"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-3 border-b flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search by name…" className="pl-9 h-9" data-testid="library-picker-search" />
          </div>
          {['all', 'fleet', 'library'].map(s => (
            <button key={s} onClick={() => setSource(s)}
              className={`text-xs px-3 py-1.5 rounded-full font-bold uppercase tracking-wider ${source === s ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-500'}`}
              data-testid={`library-picker-src-${s}`}>{s}</button>
          ))}
        </div>
        <div className="p-4 overflow-y-auto flex-1">
          {loading ? (
            <p className="text-center text-gray-400 py-10 text-sm">Loading media…</p>
          ) : filtered.length === 0 ? (
            <p className="text-center text-gray-400 py-10 text-sm">No {type}s found.</p>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
              {filtered.map((it, i) => (
                <button key={i} onClick={() => { onSelect(`${API}${it.url}`); onClose(); }}
                  className="group text-left rounded-lg overflow-hidden border-2 border-transparent hover:border-red-500 transition-colors"
                  data-testid={`library-pick-item-${i}`}>
                  <div className="aspect-square bg-gray-900 overflow-hidden">
                    {it.kind === 'video'
                      ? <video src={`${API}${it.url}`} className="w-full h-full object-cover" muted preload="metadata" />
                      : <img src={`${API}${it.url}`} alt={it.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" />}
                  </div>
                  <p className="text-[10px] text-gray-600 truncate px-1 py-1">{it.name}</p>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LibraryPickerDialog;
